/**
 * Productions on the wire (PLAN §5.1, §4.1, D22): one run of a playlist (or of
 * a single rundown) in a studio.
 *
 *   - **The config snapshot (D22, D32).** A production picks a studio and a
 *     show style (the studio's `defaultShowStyleId` when it names none).
 *     Creating it resolves the pair into the production config the engine
 *     runs (`config`) and keeps a copy of both documents as they were
 *     (`snapshot`, with `studioVersion` and `showStyleVersion`). Playout runs
 *     from `config` for the production's whole life; later edits to either
 *     apply to new productions only. A production created before D32 has
 *     `showStyleId`, `showStyleVersion` and `snapshot` null: its `config` was
 *     the whole studio config, which is the same shape.
 *   - **Pinning (§4.1).** `packageHash` is the production package the process
 *     runs from, fixed at launch. Null until the node runtime pins it (phase 4).
 *   - **Redundancy (§8, D9).** `activeNode` is the node whose process is
 *     ACTIVE, `term` bumps on every promotion. Null / 0 until phase 4–5.
 *   - **Status.** `created` (not activated, or deactivated again), `active`
 *     (its playlist is activated for playout), `ended` (terminal: no more
 *     commands). `version` is the record's own optimistic-concurrency counter.
 *
 * A production lives in the studio it was created in, so its resource scope
 * for a permission check (D14) is that studio's (`studioId`, `siteId`).
 */
import { ulidSchema } from '@acp/domain/primitives.js';
import { z } from 'zod';
import { pageLimitSchema, pageTokenSchema, PAGE_LIMIT_DESCRIPTION, PAGE_TOKEN_DESCRIPTION } from './rows.js';
import { configDocumentSchema, MAX_PRODUCTION_CONFIG_CHARS } from './configDocument.js';
import { showStyleConfigSchema } from './showStyles.js';
import { showStyleIdSchema, siteIdSchema, studioConfigSchema, studioIdSchema } from './studios.js';

/** The production config document (the resolved pair, or a pre-D32 studio config): opaque here, bounded (`configDocument.ts`). */
export const productionConfigSchema = configDocumentSchema(MAX_PRODUCTION_CONFIG_CHARS, 'production config');
export type ProductionConfig = z.infer<typeof productionConfigSchema>;

/** The two documents a production was resolved from, as they were at its creation (D32). */
export const productionSnapshotSchema = z.object({ showStyle: showStyleConfigSchema, studio: studioConfigSchema }).strict();
export type ProductionSnapshot = z.infer<typeof productionSnapshotSchema>;

export const PRODUCTION_STATUSES = ['created', 'active', 'ended'] as const;
export const productionStatusSchema = z.enum(PRODUCTION_STATUSES);
export type ProductionStatus = z.infer<typeof productionStatusSchema>;

const isoSchema = z.iso.datetime({ offset: true });
const userRefSchema = z.string().min(1).max(128);

/** A production package hash (D15): hex, as the package store names it. */
export const packageHashSchema = z.string().regex(/^[0-9a-f]{8,128}$/, 'a package hash is 8-128 lowercase hex characters');

/** A production without its config snapshot: what a listing shows. */
export const productionSummarySchema = z
  .object({
    productionId: ulidSchema,
    studioId: studioIdSchema,
    /** The studio's site when the production was created (the resource scope, D14). */
    siteId: siteIdSchema.nullable(),
    /** What it runs: exactly one of the two is set. */
    playlistId: ulidSchema.nullable(),
    rundownId: ulidSchema.nullable(),
    /** Created for rehearsal: `activate` defaults to it, and nothing is aired for real (Sofie's rehearsal mode). */
    rehearsal: z.boolean(),
    status: productionStatusSchema,
    /** The studio version the snapshot was taken from. */
    studioVersion: z.number().int().positive(),
    /** The show style it runs (D32); null for a production created before the split. */
    showStyleId: showStyleIdSchema.nullable().default(null),
    /** The show style version the snapshot was taken from; null before D32. */
    showStyleVersion: z.number().int().positive().nullable().default(null),
    /** The pinned production package (§4.1); null until phase 4 pins it. */
    packageHash: packageHashSchema.nullable(),
    /** The node whose production process is ACTIVE (§8); null until one runs it. */
    activeNode: z.string().min(1).max(128).nullable(),
    /** The failover term (D9): bumped on every promotion. */
    term: z.number().int().nonnegative(),
    /** 1 on create, +1 per write of this record. */
    version: z.number().int().positive(),
    createdAt: isoSchema,
    createdBy: userRefSchema,
    endedAt: isoSchema.nullable(),
    endedBy: userRefSchema.nullable(),
    endReason: z.string().max(500).nullable(),
  })
  .strict();
export type ProductionSummary = z.infer<typeof productionSummarySchema>;

/**
 * A production with the config it runs from (the resolved pair, D22/D32) and
 * the two documents it was resolved from (null before D32).
 */
export const productionSchema = productionSummarySchema
  .extend({ config: productionConfigSchema, snapshot: productionSnapshotSchema.nullable().default(null) })
  .strict();
export type Production = z.infer<typeof productionSchema>;

export const createProductionRequestSchema = z
  .object({
    /** Client-chosen ULID: a retried create returns the production as it is. */
    productionId: ulidSchema.optional(),
    studioId: studioIdSchema,
    /** The show style to run (D32); absent = the studio's `defaultShowStyleId` (`validation-failed` when it has none). */
    showStyleId: showStyleIdSchema.optional(),
    /** Run a playlist… */
    playlistId: ulidSchema.nullable(),
    /** …or a single rundown. Exactly one of the two. */
    rundownId: ulidSchema.nullable(),
    rehearsal: z.boolean(),
  })
  .strict()
  .refine((body) => (body.playlistId === null) !== (body.rundownId === null), {
    message: 'set exactly one of playlistId and rundownId',
    path: ['playlistId'],
  });
export type CreateProductionRequest = z.infer<typeof createProductionRequestSchema>;

export const endProductionRequestSchema = z
  .object({
    /** Why, for the as-run log; null for none. */
    reason: z.string().max(500).nullable(),
  })
  .strict();
export type EndProductionRequest = z.infer<typeof endProductionRequestSchema>;

export const productionPathSchema = z.object({ productionId: ulidSchema.describe('The production’s id (a ULID).') }).strict();

export const listProductionsQuerySchema = z
  .object({
    studioId: studioIdSchema.optional().describe('Only the productions of this studio.'),
    status: productionStatusSchema.optional().describe('Only productions in this status.'),
    pageToken: pageTokenSchema.optional().describe(PAGE_TOKEN_DESCRIPTION),
    limit: pageLimitSchema.optional().describe(PAGE_LIMIT_DESCRIPTION),
  })
  .strict();

export const productionResponseSchema = z.object({ production: productionSchema }).strict();
export const listProductionsResponseSchema = z
  .object({
    /** Newest first. */
    productions: z.array(productionSummarySchema),
    nextPageToken: pageTokenSchema.nullable(),
  })
  .strict();
